import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, LogOut, X, Upload, FileText, Sparkles, FileEdit, ExternalLink, RefreshCw } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import AdminThemeToggle from "@/components/AdminThemeToggle";
import RichTextEditor from "@/components/RichTextEditor";
import { PARTNER_PAGE_TEMPLATE } from "@/data/partnerPageTemplate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full bg-[#1A1A1A] border border-[#262626] px-4 py-2.5 text-white font-['Outfit'] text-sm placeholder:text-white/25 focus:outline-none focus:border-[#C5A059]/50 transition-colors";
const labelCls = "text-white/50 text-[11px] font-['Outfit'] uppercase tracking-wider block mb-1";
const helpCls = "text-white/30 text-[11px] font-['Outfit'] mt-1.5";

const emptyPage = {
  slug: "", title_sv: "", title_en: "",
  content_html_sv: "", content_html_en: "",
  meta_title_sv: "", meta_title_en: "",
  meta_description_sv: "", meta_description_en: "",
  og_image: "",
  hero_image_url: "", hero_title_sv: "", hero_title_en: "",
  hero_subtitle_sv: "", hero_subtitle_en: "",
  hero_cta_label_sv: "", hero_cta_label_en: "", hero_cta_url: "",
  linked_event_slug: "", visible: false,
  stock_counters: {},
};

export default function AdminPages() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token")}` });
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPage);
  const [activeLang, setActiveLang] = useState("sv");
  const [uploadingHero, setUploadingHero] = useState(false);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const heroInputRef = useRef(null);

  useEffect(() => { if (!authLoading && !user) navigate("/admin/login"); }, [authLoading, user, navigate]);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/pages`, { headers: getHeaders() });
      setPages(res.data);
    } catch { toast.error("Kunde inte hämta sidor"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (user) fetchPages(); }, [user]); // eslint-disable-line

  const openNew = () => { setShowTemplatePicker(true); };

  const startBlankPage = () => {
    setEditing(null);
    setForm(emptyPage);
    setActiveLang("sv");
    setShowTemplatePicker(false);
    setShowForm(true);
  };

  const startPartnerPage = () => {
    setEditing(null);
    setForm({ ...emptyPage, ...PARTNER_PAGE_TEMPLATE });
    setActiveLang("sv");
    setShowTemplatePicker(false);
    setShowForm(true);
    toast.success("Partner-mall förifylld — anpassa innehållet och spara");
  };

  const openEdit = async (slug) => {
    try {
      const res = await axios.get(`${API}/admin/pages/${slug}`, { headers: getHeaders() });
      setEditing(slug);
      setForm({ ...emptyPage, ...res.data });
      setShowForm(true);
      setActiveLang("sv");
    } catch { toast.error("Kunde inte ladda sidan"); }
  };

  const savePage = async (e) => {
    e.preventDefault();
    if (!form.slug.trim()) return toast.error("URL-slug krävs");
    if (!form.title_sv.trim()) return toast.error("Svensk titel krävs");
    try {
      if (editing) {
        await axios.put(`${API}/admin/pages/${editing}`, form, { headers: getHeaders() });
        toast.success("Sida uppdaterad");
      } else {
        await axios.post(`${API}/admin/pages`, form, { headers: getHeaders() });
        toast.success("Sida skapad");
      }
      setShowForm(false);
      fetchPages();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunde inte spara"); }
  };

  const toggleVisibility = async (slug, visible) => {
    await axios.put(`${API}/admin/pages/${slug}`, { visible: !visible }, { headers: getHeaders() });
    toast.success(visible ? "Sida dold" : "Sida publicerad");
    fetchPages();
  };

  const deletePage = async (slug) => {
    if (!window.confirm("Radera denna sida? Detta går inte att ångra.")) return;
    await axios.delete(`${API}/admin/pages/${slug}`, { headers: getHeaders() });
    toast.success("Sida raderad");
    fetchPages();
  };

  const reseedDefaults = async () => {
    if (!window.confirm("Synka standardsidor (juridiska + partnersidor)?\n\nDetta lägger till sidor som saknas och uppdaterar de som är flaggade för auto-uppdatering. Befintliga sidor du själv har skapat påverkas inte.")) return;
    try {
      const res = await axios.post(`${API}/admin/pages/re-seed`, {}, { headers: getHeaders() });
      const { seeded = 0, replaced = 0 } = res.data || {};
      toast.success(`Synkat — ${seeded} ny, ${replaced} uppdaterad`);
      fetchPages();
    } catch (err) { toast.error(err.response?.data?.detail || "Kunde inte synka"); }
  };

  const handleHeroUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingHero(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await axios.post(`${API}/upload/image`, fd, {
        headers: { ...getHeaders(), "Content-Type": "multipart/form-data" },
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
      setForm(prev => ({ ...prev, hero_image_url: url }));
      toast.success("Hero-bild uppladdad");
    } catch (err) { toast.error(err.response?.data?.detail || "Uppladdning misslyckades"); }
    finally { setUploadingHero(false); }
  };

  if (authLoading || !user) return <div className="min-h-screen bg-[#0A0A0A]" />;

  let pageTitle = "Landningssidor";
  if (showForm && editing) pageTitle = "Redigera sida";
  else if (showForm) pageTitle = "Ny sida";

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <nav className="bg-[#141414] border-b border-[#262626] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/admin" className="text-[#C5A059] font-semibold text-sm tracking-[0.3em] uppercase font-['Outfit']">SERAVA ADMIN</Link>
            <div className="hidden md:flex gap-6">
              <Link to="/admin" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Översikt</Link>
              <Link to="/admin/events" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Events</Link>
              <Link to="/admin/pages" className="text-white text-xs tracking-wider uppercase font-['Outfit']">Sidor</Link>
              <Link to="/admin/orders" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Ordrar</Link>
              <Link to="/admin/contest-entries" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Tävling</Link>
              <Link to="/admin/settings" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit']">Inställningar</Link>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white/40 hover:text-white text-xs font-['Outfit']">Visa sajten</Link>
            <AdminThemeToggle />
            <button onClick={async () => { await logout(); navigate("/admin/login"); }} className="text-white/40 hover:text-red-400"><LogOut size={18} /></button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* Template picker modal */}
        {showTemplatePicker && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => setShowTemplatePicker(false)} data-testid="template-picker-modal">
            <div className="bg-[#141414] border border-[#262626] max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#262626]">
                <h2 className="font-['Playfair_Display'] text-xl text-white">Välj mall</h2>
                <button onClick={() => setShowTemplatePicker(false)} className="text-white/40 hover:text-white" data-testid="close-template-picker"><X size={18} /></button>
              </div>
              <div className="p-6 grid sm:grid-cols-2 gap-4">
                <button onClick={startBlankPage} data-testid="template-blank" className="text-left border border-[#262626] hover:border-[#C5A059]/60 bg-[#0A0A0A] p-6 transition-colors group">
                  <div className="w-12 h-12 border border-[#262626] group-hover:border-[#C5A059]/60 flex items-center justify-center mb-4 transition-colors">
                    <FileText size={20} className="text-white/60 group-hover:text-[#C5A059]" />
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] text-xl text-white mb-2">Tom sida</h3>
                  <p className="text-white/50 text-xs font-['Outfit'] leading-relaxed">Börja från noll. Du fyller i hero, innehåll och SEO-fält själv.</p>
                </button>
                <button onClick={startPartnerPage} data-testid="template-partner" className="text-left border border-[#C5A059]/30 hover:border-[#C5A059] bg-gradient-to-br from-[#C5A059]/[0.06] to-transparent p-6 transition-colors group relative">
                  <span className="absolute top-3 right-3 text-[10px] tracking-[0.18em] uppercase text-[#C5A059] font-['Outfit'] flex items-center gap-1"><Sparkles size={10} /> Förifylld</span>
                  <div className="w-12 h-12 border border-[#C5A059]/40 group-hover:border-[#C5A059] flex items-center justify-center mb-4 transition-colors bg-[#C5A059]/5">
                    <FileEdit size={20} className="text-[#C5A059]" />
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] text-xl text-white mb-2">Partner-sida</h3>
                  <p className="text-white/60 text-xs font-['Outfit'] leading-relaxed">Konverteringsoptimerad landningssida med hero, stats, fördelar, process, testimonials, FAQ och CTA. SV + EN.</p>
                </button>
              </div>
              <div className="px-6 pb-5 pt-1">
                <p className="text-white/30 text-[11px] font-['Outfit']">Du kan ändra all text och alla bilder efter att mallen är förifylld.</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <h1 className="font-['Playfair_Display'] text-3xl text-white">{pageTitle}</h1>
          <div className="flex gap-3">
            {showForm && editing && form.slug && (
              <a href={`/p/${form.slug}`} target="_blank" rel="noopener noreferrer" className="btn-secondary px-5 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit'] flex items-center gap-2">
                <Eye size={14} /> Förhandsvisa
              </a>
            )}
            {!showForm && <button onClick={reseedDefaults} data-testid="reseed-pages-btn" className="btn-secondary px-5 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit'] flex items-center gap-2" title="Lägger till standardsidor som saknas (juridiska + partnersidor) utan att radera dina egna ändringar"><RefreshCw size={14} /> Synka standardsidor</button>}
            {!showForm && <button onClick={openNew} data-testid="add-page-btn" className="btn-primary px-6 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit'] flex items-center gap-2"><Plus size={14} /> Ny landningssida</button>}
            {showForm && <button onClick={() => setShowForm(false)} className="btn-secondary px-6 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit'] flex items-center gap-2"><X size={14} /> Avbryt</button>}
          </div>
        </div>

        {showForm ? (
          <form onSubmit={savePage} className="space-y-6 mb-12">
            {/* Intro */}
            <div className="border border-[#C5A059]/30 bg-[#C5A059]/5 p-5">
              <p className="text-[#C5A059] text-xs tracking-[0.2em] uppercase font-['Outfit'] font-semibold mb-2">Bygg en landningssida</p>
              <p className="text-white/60 font-['Outfit'] text-sm leading-relaxed">
                Sidan blir tillgänglig på <span className="text-[#C5A059]">seravasports.com/p/{form.slug || "din-slug"}</span> när du publicerar den.
                Fyll i innehåll på både svenska och engelska om du vill att sidan ska visas på rätt språk för besökare.
              </p>
            </div>

            {/* 1. URL & visibility */}
            <div className="bg-[#141414] border border-[#262626] p-6">
              <div className="flex items-start gap-3 mb-4">
                <span className="text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/30 px-2 py-0.5 text-xs font-['Outfit'] font-semibold">STEG 1</span>
                <h3 className="text-white text-sm font-['Outfit'] font-medium uppercase tracking-[0.15em]">URL & publicering</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>URL-slug *</label>
                  <input value={form.slug} onChange={e => setForm({...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-")})} placeholder="t.ex. champions-league-2026" required className={inputCls} />
                  <p className={helpCls}>Visas i URL: <span className="text-[#C5A059]">/p/{form.slug || "din-slug"}</span></p>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={form.visible} onChange={e => setForm({...form, visible: e.target.checked})} className="w-4 h-4 accent-[#C5A059]" />
                    <span className="text-white/70 font-['Outfit'] text-sm">Publicerad (synlig publikt)</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Language tabs */}
            <div className="bg-[#141414] border border-[#262626] p-6">
              <div className="flex items-start gap-3 mb-4">
                <span className="text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/30 px-2 py-0.5 text-xs font-['Outfit'] font-semibold">STEG 2</span>
                <h3 className="text-white text-sm font-['Outfit'] font-medium uppercase tracking-[0.15em]">Innehåll</h3>
              </div>
              <div className="flex gap-1 mb-5 border-b border-[#262626]">
                {[{ k: "sv", label: "🇸🇪 Svenska" }, { k: "en", label: "🇬🇧 Engelska" }].map(t => (
                  <button key={t.k} type="button" onClick={() => setActiveLang(t.k)}
                    className={`px-5 py-2.5 text-xs tracking-[0.15em] uppercase font-['Outfit'] border-b-2 -mb-px transition-colors ${activeLang === t.k ? "border-[#C5A059] text-[#C5A059]" : "border-transparent text-white/40 hover:text-white"}`}>
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Per-language fields */}
              <div className="space-y-5">
                <div>
                  <label className={labelCls}>Sidtitel ({activeLang === "sv" ? "Svenska" : "English"}) {activeLang === "sv" && "*"}</label>
                  <input value={form[`title_${activeLang}`]} onChange={e => setForm({...form, [`title_${activeLang}`]: e.target.value})} placeholder={activeLang === "sv" ? "t.ex. Champions League 2026" : "e.g. Champions League 2026"} required={activeLang === "sv"} className={inputCls} />
                  <p className={helpCls}>Huvudrubriken på sidan + flik-titel i webbläsaren</p>
                </div>

                {/* Hero section */}
                <div className="border border-[#262626] p-4 bg-[#0F0F0F]">
                  <p className="text-[#C5A059]/80 text-[11px] font-['Outfit'] uppercase tracking-wider mb-3">Hero-banner (valfri)</p>
                  <div className="space-y-3">
                    <div>
                      <label className={labelCls}>Hero-rubrik</label>
                      <input value={form[`hero_title_${activeLang}`]} onChange={e => setForm({...form, [`hero_title_${activeLang}`]: e.target.value})} placeholder={activeLang === "sv" ? "Stora rubriken över bilden" : "Big headline over the image"} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Hero-underrubrik</label>
                      <input value={form[`hero_subtitle_${activeLang}`]} onChange={e => setForm({...form, [`hero_subtitle_${activeLang}`]: e.target.value})} placeholder={activeLang === "sv" ? "Liten text under rubriken" : "Small text under the headline"} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Knapp-text</label>
                      <input value={form[`hero_cta_label_${activeLang}`]} onChange={e => setForm({...form, [`hero_cta_label_${activeLang}`]: e.target.value})} placeholder={activeLang === "sv" ? "t.ex. Köp nu" : "e.g. Buy Now"} className={inputCls} />
                    </div>
                  </div>
                </div>

                {/* Rich text editor */}
                <div>
                  <label className={labelCls}>Brödtext / sidans innehåll</label>
                  <RichTextEditor
                    key={activeLang}
                    value={form[`content_html_${activeLang}`]}
                    onChange={(html) => setForm(prev => ({ ...prev, [`content_html_${activeLang}`]: html }))}
                    placeholder={activeLang === "sv" ? "Skriv sidans innehåll här..." : "Write the page content here..."}
                  />
                </div>

                {/* SEO meta */}
                <div className="border border-[#262626] p-4 bg-[#0F0F0F] space-y-3">
                  <p className="text-[#C5A059]/80 text-[11px] font-['Outfit'] uppercase tracking-wider">SEO-metadata</p>
                  <div>
                    <label className={labelCls}>Meta-titel</label>
                    <input value={form[`meta_title_${activeLang}`]} onChange={e => setForm({...form, [`meta_title_${activeLang}`]: e.target.value})} placeholder={activeLang === "sv" ? "Lämna tom för att använda sidtiteln" : "Leave empty to use page title"} className={inputCls} />
                    <p className={helpCls}>Visas i Google-sökresultat & webbläsarens flik (60-70 tecken rekommenderas)</p>
                  </div>
                  <div>
                    <label className={labelCls}>Meta-beskrivning</label>
                    <textarea value={form[`meta_description_${activeLang}`]} onChange={e => setForm({...form, [`meta_description_${activeLang}`]: e.target.value})} placeholder={activeLang === "sv" ? "Kort beskrivning som visas i Google" : "Short description shown in Google"} rows={2} className={inputCls} />
                    <p className={helpCls}>150–160 tecken rekommenderas</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Hero image & event link (language-independent) */}
            <div className="bg-[#141414] border border-[#262626] p-6 space-y-4">
              <div className="flex items-start gap-3 mb-1">
                <span className="text-[#C5A059] bg-[#C5A059]/10 border border-[#C5A059]/30 px-2 py-0.5 text-xs font-['Outfit'] font-semibold">STEG 3</span>
                <h3 className="text-white text-sm font-['Outfit'] font-medium uppercase tracking-[0.15em]">Bilder & länkar</h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls}>Hero-bild</label>
                  <input ref={heroInputRef} type="file" accept="image/*" onChange={handleHeroUpload} className="hidden" />
                  <button type="button" onClick={() => heroInputRef.current?.click()} disabled={uploadingHero}
                    className="w-full flex items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-[#262626] hover:border-[#C5A059]/40 text-white/40 hover:text-white/60 transition-colors cursor-pointer">
                    {uploadingHero ? <span className="animate-spin w-5 h-5 border-2 border-[#C5A059] border-t-transparent rounded-full" /> : <Upload size={18} />}
                    <span className="text-sm font-['Outfit']">{uploadingHero ? "Laddar upp..." : "Ladda upp hero-bild"}</span>
                  </button>
                  <input value={form.hero_image_url} onChange={e => setForm({...form, hero_image_url: e.target.value})} placeholder="eller klistra in URL" className={`${inputCls} mt-2`} />
                </div>
                <div>
                  <label className={labelCls}>Förhandsvisning</label>
                  {form.hero_image_url ? (
                    <div className="border border-[#262626] h-32 overflow-hidden bg-black"><img src={form.hero_image_url} alt="Hero" className="w-full h-full object-cover" /></div>
                  ) : (
                    <div className="border border-[#262626] h-32 flex items-center justify-center bg-[#1A1A1A] text-white/15">Ingen bild</div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Knapp-länk (URL)</label>
                  <input value={form.hero_cta_url} onChange={e => setForm({...form, hero_cta_url: e.target.value})} placeholder="https://... eller /event/min-slug" className={inputCls} />
                  <p className={helpCls}>Vart hero-knappen leder. Lämna tom för att dölja knappen.</p>
                </div>
                <div>
                  <label className={labelCls}>Länkad event-slug (valfri)</label>
                  <input value={form.linked_event_slug || ""} onChange={e => setForm({...form, linked_event_slug: e.target.value})} placeholder="t.ex. champions-league-final" className={inputCls} />
                  <p className={helpCls}>Visar en &ldquo;Se event&rdquo;-länk längst ner på sidan</p>
                </div>
              </div>

              <div>
                <label className={labelCls}>OG-bild (för social delning)</label>
                <input value={form.og_image} onChange={e => setForm({...form, og_image: e.target.value})} placeholder="URL till bild som visas när sidan delas på Facebook/LinkedIn" className={inputCls} />
                <p className={helpCls}>Lämna tom för att använda hero-bilden</p>
              </div>

              {/* Stock counters */}
              <div className="border border-[#262626] p-5 bg-[#0E0E0E]">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <label className={labelCls} style={{marginBottom:0}}>Lagerräknare</label>
                    <p className={helpCls} style={{marginTop:6}}>Sätt en nyckel (t.ex. <code>stockholm_vip</code>) och antal kvar. I innehållet använder du <code>{`{{stock:nyckel}}`}</code> där pillen ska visas. Sätt till 0 för &ldquo;Slutsåld&rdquo;.</p>
                  </div>
                  <button type="button" onClick={() => {
                    const newKey = window.prompt("Nyckel för räknaren (a-z, 0-9, _):", "");
                    if (!newKey) return;
                    const cleanKey = newKey.trim().toLowerCase().replace(/[^a-z0-9_-]/g, "");
                    if (!cleanKey) { toast.error("Ogiltig nyckel"); return; }
                    if (form.stock_counters && cleanKey in form.stock_counters) { toast.error("Nyckeln finns redan"); return; }
                    setForm({...form, stock_counters: {...(form.stock_counters || {}), [cleanKey]: 0}});
                  }} data-testid="add-stock-counter-btn" className="btn-secondary px-3 py-2 text-[10px] tracking-[0.15em] uppercase font-['Outfit'] flex items-center gap-1 shrink-0">
                    <Plus size={12} /> Lägg till
                  </button>
                </div>
                {(!form.stock_counters || Object.keys(form.stock_counters).length === 0) ? (
                  <p className="text-white/30 text-xs font-['Outfit'] italic">Inga räknare än. Klicka &ldquo;Lägg till&rdquo; för att skapa en.</p>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(form.stock_counters).map(([key, value]) => (
                      <div key={key} className="flex gap-2 items-center" data-testid={`stock-row-${key}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-white/40 text-[10px] font-['Outfit'] tracking-[0.15em] uppercase mb-1">Nyckel</div>
                          <div className="font-mono text-sm text-white bg-[#0A0A0A] border border-[#262626] px-3 py-2 truncate">{key}</div>
                        </div>
                        <div className="w-28 shrink-0">
                          <div className="text-white/40 text-[10px] font-['Outfit'] tracking-[0.15em] uppercase mb-1">Antal kvar</div>
                          <input type="number" min="0" value={value} onChange={e => setForm({...form, stock_counters: {...form.stock_counters, [key]: parseInt(e.target.value, 10) || 0}})} className={inputCls} data-testid={`stock-input-${key}`} />
                        </div>
                        <button type="button" onClick={() => {
                          const next = {...form.stock_counters};
                          delete next[key];
                          setForm({...form, stock_counters: next});
                        }} className="p-2 text-white/30 hover:text-red-400 transition-colors self-end mb-[1px]" title="Ta bort" data-testid={`stock-remove-${key}`}><Trash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Save */}
            <div className="flex gap-3 pt-2">
              <button type="submit" data-testid="save-page-btn" className="btn-primary px-8 py-3.5 text-xs tracking-[0.15em] uppercase font-['Outfit']">
                {editing ? "Spara ändringar" : "Skapa sida"}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="btn-secondary px-8 py-3.5 text-xs tracking-[0.15em] uppercase font-['Outfit']">Avbryt</button>
            </div>
          </form>
        ) : (
          /* List */
          <div className="space-y-2">
            {loading && <p className="text-white/40 text-sm">Laddar...</p>}

            {/* System pages (hardcoded React pages — read-only, shown so admins see the full site) */}
            {!loading && (
              <>
                <p className="text-white/30 text-[10px] tracking-[0.2em] uppercase font-['Outfit'] mt-2 mb-2">System­sidor (kodade i appen)</p>
                {[
                  { slug: "/", title: "Startsida", url: "/", description: "Hem — events, om oss, kontakt", editPath: null },
                  { slug: "/tavla", title: "Tävla & vinn", url: "/tavla", description: "Tävlingssida — slogan + Facebook-följ", editPath: "/admin/contest-entries" },
                ].map((sp) => (
                  <div key={sp.slug} className="bg-[#141414] border border-[#262626] border-dashed p-5 flex items-center justify-between hover:border-[#C5A059]/30 transition-colors" data-testid={`system-page-${sp.slug.replace(/\//g, "")}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="text-white font-['Outfit'] font-medium truncate">{sp.title}</h3>
                        <span className="text-[10px] text-[#C5A059] border border-[#C5A059]/40 px-2 py-0.5 font-['Outfit'] uppercase tracking-wider">System</span>
                      </div>
                      <p className="text-white/40 text-xs font-['Outfit']">{sp.url} <span className="text-white/25">· {sp.description}</span></p>
                    </div>
                    <div className="flex gap-2 ml-4">
                      <a href={sp.url} target="_blank" rel="noopener noreferrer" className="p-2 text-white/30 hover:text-[#C5A059]" title="Öppna live i ny flik" data-testid={`system-view-live-${sp.slug.replace(/\//g, "")}`}>
                        <ExternalLink size={16} />
                      </a>
                      {sp.editPath && (
                        <Link to={sp.editPath} className="p-2 text-white/30 hover:text-[#C5A059]" title="Hantera">
                          <Pencil size={16} />
                        </Link>
                      )}
                    </div>
                  </div>
                ))}
                <p className="text-white/30 text-[10px] tracking-[0.2em] uppercase font-['Outfit'] mt-6 mb-2">CMS-sidor (redigerbara)</p>
              </>
            )}

            {!loading && pages.length === 0 && (
              <div className="text-center py-16 border border-dashed border-[#262626]">
                <FileText size={36} className="mx-auto text-white/15 mb-3" />
                <p className="text-white/40 font-['Outfit'] text-sm mb-4">Inga landningssidor än</p>
                <button onClick={openNew} className="btn-primary px-6 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit']">Skapa första sidan</button>
              </div>
            )}
            {pages.map(p => (
              <div key={p.slug} className="bg-[#141414] border border-[#262626] p-5 flex items-center justify-between hover:border-[#C5A059]/30 transition-colors">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-white font-['Outfit'] font-medium truncate">{p.title_sv || p.title_en || p.slug}</h3>
                    {p.visible ? (
                      <span className="text-[10px] text-emerald-400 border border-emerald-500/30 px-2 py-0.5 font-['Outfit'] uppercase tracking-wider">Publicerad</span>
                    ) : (
                      <span className="text-[10px] text-white/40 border border-white/20 px-2 py-0.5 font-['Outfit'] uppercase tracking-wider">Utkast</span>
                    )}
                  </div>
                  <p className="text-white/40 text-xs font-['Outfit']">/p/{p.slug}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <a href={`/p/${p.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-white/30 hover:text-[#C5A059]" title="Öppna live i ny flik" data-testid={`page-view-live-${p.slug}`}>
                    <ExternalLink size={16} />
                  </a>
                  <button onClick={() => toggleVisibility(p.slug, p.visible)} className="p-2 text-white/30 hover:text-white" title={p.visible ? "Dölj" : "Publicera"}>
                    {p.visible ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                  <button onClick={() => openEdit(p.slug)} className="p-2 text-white/30 hover:text-[#C5A059]" title="Redigera"><Pencil size={16} /></button>
                  <button onClick={() => deletePage(p.slug)} className="p-2 text-white/30 hover:text-red-400" title="Radera"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
